import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface UserPreference {
  id: number;
  userId: string;
  componentName: string;
  preferenceKey: string;
  preferenceValue: unknown; // JSONB - can be string, number, boolean, object, array, or null
  metadata: string | null;
  createdAt: string;
  updatedAt: string;
}

// Preferences organized by component: { [componentName]: { [preferenceKey]: preferenceValue } }
export type PreferencesMap = Record<string, Record<string, unknown>>;

export interface UserPreferencesState {
  // State
  preferences: UserPreference[];
  isLoading: boolean;
  error: string | null;
  lastFetched: number | null; // timestamp

  // Actions
  setPreferences: (preferences: UserPreference[]) => void;
  addPreference: (preference: UserPreference) => void;
  updatePreference: (preference: UserPreference) => void;
  removePreference: (id: number) => void;
  clearPreferences: () => void;
  setLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
  
  // Helper getters
  getPreference: (componentName: string, preferenceKey: string) => unknown | null;
  getPreferencesByComponent: (componentName: string) => Record<string, unknown>;
  getAllPreferences: () => PreferencesMap;
}

export const useUserPreferencesStore = create<UserPreferencesState>()(
  persist(
    (set, get) => ({
      // Initial state
      preferences: [],
      isLoading: false,
      error: null,
      lastFetched: null,

      // Actions
      setPreferences: (preferences) =>
        set({
          preferences,
          lastFetched: Date.now(),
          error: null,
        }),

      addPreference: (preference) =>
        set((state) => ({
          preferences: [...state.preferences, preference],
        })),

      updatePreference: (preference) =>
        set((state) => ({
          preferences: state.preferences.map((p) =>
            p.id === preference.id ? preference : p
          ),
        })),

      removePreference: (id) =>
        set((state) => ({
          preferences: state.preferences.filter((p) => p.id !== id),
        })),

      clearPreferences: () =>
        set({
          preferences: [],
          lastFetched: null,
          error: null,
        }),

      setLoading: (isLoading) => set({ isLoading }),

      setError: (error) => set({ error }),

      // Helper getters
      getPreference: (componentName, preferenceKey) => {
        const state = get();
        const preference = state.preferences.find(
          (p) => p.componentName === componentName && p.preferenceKey === preferenceKey
        );
        return preference?.preferenceValue ?? null;
      },

      getPreferencesByComponent: (componentName) => {
        const state = get();
        const componentPreferences = state.preferences.filter(
          (p) => p.componentName === componentName
        );
        const result: Record<string, unknown> = {};
        componentPreferences.forEach((p) => {
          result[p.preferenceKey] = p.preferenceValue;
        });
        return result;
      },

      getAllPreferences: () => {
        const state = get();
        const result: PreferencesMap = {};
        state.preferences.forEach((p) => {
          if (!result[p.componentName]) {
            result[p.componentName] = {};
          }
          result[p.componentName][p.preferenceKey] = p.preferenceValue;
        });
        return result;
      },
    }),
    {
      name: 'prototype-user-preferences', // localStorage key
      partialize: (state) => ({
        preferences: state.preferences,
        lastFetched: state.lastFetched,
      }),
    }
  )
);

// Selectors
export const selectPreference = (componentName: string, preferenceKey: string) => 
  (state: UserPreferencesState) => state.getPreference(componentName, preferenceKey);

export const selectComponentPreferences = (componentName: string) =>
  (state: UserPreferencesState) => state.getPreferencesByComponent(componentName);

export const selectAllPreferences = (state: UserPreferencesState) => 
  state.getAllPreferences();
